import { StandardEvents } from '@shopify/events';
import { lockScroll, unlockScroll } from '@theme/utilities';

/**
 * Lobos — akceptacja regulaminu w koszyku (szuflada i strona koszyka), zamiast aplikacji TnC (5.10.2026).
 * Markup: snippets/lobos-terms.liquid; bramka przycisków: .cart__ctas[data-lobos-terms-gate] w snippets/cart-summary.liquid.
 * Moduł ładuje się na każdej stronie (snippets/scripts.liquid), bo skrypty wstawione do szuflady po dodaniu
 * pierwszego produktu (morph) się nie wykonują.
 *
 * - Pole nigdy nie jest zaznaczone domyślnie. Zaznaczenie = POST /cart/update.js z atrybutem „Akceptacja regulaminu”:
 *   „TAK · RRRR-MM-DD GG:MM:SS (Europe/Warsaw) · <wersja>”; odznaczenie czyści atrybut (pusta wartość).
 *   Atrybut pamięta wybór klienta tylko w tym koszyku: gdy koszyk staje się pusty, czyścimy go (zdarzenie koszyka
 *   albo znacznik data-lobos-terms-reset z Liquid po wczytaniu strony), więc nowy koszyk zaczyna się od pustego pola.
 * - Bez zaznaczenia „Do kasy” ma aria-disabled, szybkie płatności (Shop Pay, Apple Pay, Google Pay) są `inert`
 *   i przykryte warstwą; dotknięcie pokazuje krótką podpowiedź przy polu.
 * - Link „Regulamin sklepu” otwiera okno (dialog) na tej samej stronie. Treść wczytujemy dopiero po kliknięciu:
 *   <adres linku>?section_id=lobos-tresc-strony (PL: /pages/terms, EN: /en/pages/terms). Zamykanie: „Zamknij”,
 *   Esc, kliknięcie w tło; fokus wraca na link. Ctrl/Cmd/Shift + klik i środkowy przycisk otwierają zwykłą stronę.
 * - Gdy skrypt się nie wczyta, „Do kasy” działa jak dawniej (bez blokady), a link prowadzi na stronę regulaminu.
 */

const KEY = 'Akceptacja regulaminu';
const CONTENT_SECTION = 'lobos-tresc-strony';

/** @type {boolean | null} wybór klienta w tej karcie, którego serwer jeszcze nie potwierdził (null = obowiązuje stan z serwera) */
let desired = null;

/** @type {Promise<void>} kolejka zapisów, żeby szybkie zaznacz/odznacz zapisały się po kolei */
let queue = Promise.resolve();

let pending = 0;
let saveSeq = 0;
let installed = false;

/** @type {Map<string, Promise<string>>} treść regulaminu wg adresu (pobierana raz na wczytanie strony) */
const contentCache = new Map();

/** @type {WeakMap<HTMLDialogElement, HTMLElement>} link, na który wraca fokus po zamknięciu okna */
const returnFocus = new WeakMap();

/** @type {WeakSet<HTMLDialogElement>} */
const boundDialogs = new WeakSet();

/** Data i godzina w strefie Europe/Warsaw: RRRR-MM-DD GG:MM:SS. */
function warsawTimestamp() {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Europe/Warsaw',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(new Date());
  /** @param {string} type */
  const part = (type) => parts.find((item) => item.type === type)?.value || '00';
  return `${part('year')}-${part('month')}-${part('day')} ${part('hour')}:${part('minute')}:${part('second')}`;
}

function updateUrl() {
  const base = typeof Theme !== 'undefined' && Theme.routes?.cart_update_url ? Theme.routes.cart_update_url : '/cart/update';
  return `${base}.js`;
}

/**
 * @param {string} key
 * @param {string} value
 */
async function postAttribute(key, value) {
  const response = await fetch(updateUrl(), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ attributes: { [key]: value } }),
  });
  if (!response.ok) throw new Error(`cart/update ${response.status}`);
}

/** @returns {LobosTerms[]} */
function allTerms() {
  return [...document.querySelectorAll('lobos-terms')].filter((element) => element instanceof LobosTerms);
}

function syncAll() {
  for (const element of allTerms()) element.sync();
}

/**
 * Zapis wyboru w atrybucie koszyka (kolejka, jedna ponowna próba).
 * Po potwierdzeniu przez serwer stan z serwera znów obowiązuje; bez zapisu pole wraca do odznaczonego.
 * @param {boolean} accepted
 * @param {string} key
 * @param {string} version
 */
function save(accepted, key, version) {
  const value = accepted ? `TAK · ${warsawTimestamp()} (Europe/Warsaw) · ${version || 'aktualna'}` : '';
  const ticket = ++saveSeq;
  pending += 1;
  queue = queue
    .then(() => postAttribute(key, value))
    .catch(() => postAttribute(key, value))
    .then(
      () => {
        if (ticket !== saveSeq) return;
        desired = null;
        for (const element of allTerms()) element.commit(accepted);
      },
      (error) => {
        console.warn('[lobos-terms] Nie zapisano akceptacji regulaminu:', error);
        if (ticket !== saveSeq) return;
        desired = null;
        for (const element of allTerms()) {
          element.commit(false);
          if (accepted) element.showHint(false);
        }
      }
    )
    .finally(() => {
      pending -= 1;
    });
  return queue;
}

/** Pusty koszyk: akceptacja z poprzedniego koszyka przestaje obowiązywać. */
function resetAcceptance() {
  desired = null;
  for (const element of allTerms()) element.commit(false);
  save(false, KEY, '');
}

/**
 * @param {Element} gate
 * @param {boolean} locked
 */
function setLocked(gate, locked) {
  gate.toggleAttribute('data-locked', locked);
  // wszystkie przyciski kasy w bramce (także kopie, które wstawiają aplikacje, np. TnC)
  for (const checkout of gate.querySelectorAll('.cart__checkout-button')) {
    if (locked) checkout.setAttribute('aria-disabled', 'true');
    else checkout.removeAttribute('aria-disabled');
  }
  for (const wallets of gate.querySelectorAll('.additional-checkout-buttons')) {
    wallets.toggleAttribute('inert', locked);
  }
}

/**
 * Pole regulaminu dla danej bramki (ten sam kontener: szuflada albo podsumowanie koszyka).
 * @param {Element} gate
 * @returns {LobosTerms | null}
 */
function termsFor(gate) {
  const parent = gate.parentElement;
  if (!parent) return null;
  for (const element of parent.children) {
    if (element instanceof LobosTerms) return element;
  }
  return null;
}

/* ---------- okno z regulaminem ---------- */

/** @param {HTMLDialogElement} dialog */
function closeTerms(dialog) {
  if (dialog.open) dialog.close();
}

/** @param {HTMLDialogElement} dialog */
function bindDialog(dialog) {
  if (boundDialogs.has(dialog)) return;
  boundDialogs.add(dialog);

  dialog.addEventListener('click', (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (target === dialog) {
      // klik w tło (::backdrop trafia w sam <dialog>), ale nie w obszar okna
      const rect = dialog.getBoundingClientRect();
      const inside =
        event.clientX >= rect.left &&
        event.clientX <= rect.right &&
        event.clientY >= rect.top &&
        event.clientY <= rect.bottom;
      if (inside) return;
      // szuflada koszyka nie może uznać tego kliknięcia za klik poza nią
      event.stopPropagation();
      closeTerms(dialog);
      return;
    }
    if (target?.closest('[data-lobos-terms-close]')) {
      event.stopPropagation();
      closeTerms(dialog);
    }
  });

  dialog.addEventListener('close', () => {
    // szuflada przy zmianie układu przestawia okno na wierzch (close + showModal) — wtedy nic nie robimy
    if (dialog.open) return;
    unlockScroll(dialog);
    const link = returnFocus.get(dialog);
    const fallback = dialog.closest('lobos-terms')?.querySelector('[data-lobos-terms-open]');
    const target = link?.isConnected ? link : fallback;
    if (target instanceof HTMLElement) target.focus({ preventScroll: true });
  });
}

/** @param {HTMLDialogElement} dialog */
function loadTerms(dialog) {
  const body = dialog.querySelector('[data-lobos-terms-body]');
  if (!(body instanceof HTMLElement)) return;
  const src = body.dataset.src || '';
  if (!src || body.dataset.loaded === src) return;

  const url = new URL(src, window.location.href);
  url.searchParams.set('section_id', CONTENT_SECTION);
  const key = url.toString();

  let request = contentCache.get(key);
  if (!request) {
    request = fetch(key, { headers: { Accept: 'text/html' } })
      .then((response) => {
        if (!response.ok) throw new Error(`terms ${response.status}`);
        return response.text();
      })
      .then((html) => {
        const content = new DOMParser().parseFromString(html, 'text/html').querySelector('.lobos-tresc');
        if (!content || !content.textContent?.trim()) throw new Error('terms: empty');
        return content.innerHTML;
      });
    // po błędzie następne otwarcie próbuje jeszcze raz
    request.catch(() => contentCache.delete(key));
    contentCache.set(key, request);
  }

  body.setAttribute('aria-busy', 'true');
  request
    .then((html) => {
      body.innerHTML = html;
      body.dataset.loaded = src;
      body.scrollTop = 0;
    })
    .catch((error) => {
      console.warn('[lobos-terms] Nie wczytano regulaminu:', error);
      const template = dialog.querySelector('template[data-lobos-terms-error]');
      if (template instanceof HTMLTemplateElement) body.replaceChildren(template.content.cloneNode(true));
    })
    .finally(() => body.removeAttribute('aria-busy'));
}

/**
 * @param {HTMLDialogElement} dialog
 * @param {HTMLElement} link
 * @returns {boolean}
 */
function openTerms(dialog, link) {
  bindDialog(dialog);
  returnFocus.set(dialog, link);
  if (!dialog.open) {
    try {
      dialog.showModal();
    } catch (error) {
      console.warn('[lobos-terms] Nie otwarto okna regulaminu:', error);
      return false;
    }
  }
  lockScroll(dialog);
  const body = dialog.querySelector('[data-lobos-terms-body]');
  if (body instanceof HTMLElement) body.focus({ preventScroll: true });
  loadTerms(dialog);
  return true;
}

/* ---------- blokada kasy ---------- */

function install() {
  if (installed) return;
  installed = true;

  // Zablokowane: klik w „Do kasy” albo w warstwę nad szybkimi płatnościami -> podpowiedź, bez przejścia do kasy.
  // Odblokowane, ale zapis akceptacji jeszcze trwa: kasa rusza dopiero po zapisie.
  window.addEventListener(
    'click',
    (event) => {
      const target = event.target instanceof Element ? event.target : null;
      const gate = target?.closest('[data-lobos-terms-gate]');
      if (!gate || !target) return;
      const control = target.closest('.cart__checkout-button, [data-lobos-terms-shield]');
      if (!control) return;

      if (gate.hasAttribute('data-locked')) {
        event.preventDefault();
        event.stopImmediatePropagation();
        termsFor(gate)?.showHint();
        return;
      }

      if (pending > 0 && control instanceof HTMLButtonElement && control.form) {
        event.preventDefault();
        event.stopImmediatePropagation();
        const form = control.form;
        // przycisk „checkout” jako wysyłający (name=checkout przenosi do kasy); kopia typu button -> oryginał
        const submitter =
          control.type === 'submit'
            ? control
            : gate.querySelector('button[type="submit"][name="checkout"]') ||
              form.querySelector('button[type="submit"][name="checkout"]');
        queue.finally(() => {
          // zapis się nie udał albo klient odznaczył pole: nadal blokada
          if (gate.hasAttribute('data-locked')) {
            termsFor(gate)?.showHint();
            return;
          }
          if (!form.isConnected) return;
          if (typeof form.requestSubmit === 'function') form.requestSubmit(submitter || undefined);
          else form.submit();
        });
      }
    },
    true
  );

  // Wysyłka formularza koszyka przyciskiem kasy przy braku akceptacji (np. klawiatura).
  window.addEventListener(
    'submit',
    (event) => {
      const submitter = /** @type {SubmitEvent} */ (event).submitter;
      const gate = submitter instanceof Element ? submitter.closest('[data-lobos-terms-gate][data-locked]') : null;
      if (!gate) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      termsFor(gate)?.showHint();
    },
    true
  );

  // Esc zamyka najpierw okno regulaminu, a nie szufladę pod nim.
  document.addEventListener(
    'keydown',
    (event) => {
      if (event.key !== 'Escape' || event.isComposing) return;
      const dialog = document.querySelector('dialog[data-lobos-terms-dialog][open]');
      if (!(dialog instanceof HTMLDialogElement)) return;
      event.preventDefault();
      event.stopPropagation();
      closeTerms(dialog);
    },
    true
  );

  // Szuflada przerysowuje się po zmianach w koszyku: przywracamy niezapisany jeszcze wybór,
  // a gdy koszyk jest pusty, czyścimy akceptację (nowy koszyk = puste pole).
  document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
    const promise = /** @type {any} */ (event).promise;
    const later = () => setTimeout(syncAll, 80);
    if (!promise || typeof promise.then !== 'function') {
      later();
      return;
    }
    promise
      .then((/** @type {any} */ result) => {
        const cart = result?.cart;
        const detail = result?.detail;
        let count = null;
        if (typeof cart?.totalQuantity === 'number') count = cart.totalQuantity;
        else if (detail?.source === 'cart-items-component' && typeof detail?.itemCount === 'number') count = detail.itemCount;
        if (count === 0) resetAcceptance();
      })
      .catch(() => {})
      .finally(later);
  });

  window.addEventListener('pageshow', (event) => {
    if (event.persisted) syncAll();
  });

  // Pusty koszyk z akceptacją z wcześniejszego koszyka (np. wyczyszczony poza tą kartą): czyścimy od razu.
  if (document.querySelector('script[data-lobos-terms-reset]')) resetAcceptance();
}

class LobosTerms extends HTMLElement {
  connectedCallback() {
    install();
    this.addEventListener('change', this.#onChange);
    this.addEventListener('click', this.#onClick);
    this.sync();
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.#onChange);
    this.removeEventListener('click', this.#onClick);
  }

  /** @returns {HTMLInputElement | null} */
  get input() {
    const input = this.querySelector('input[type="checkbox"]');
    return input instanceof HTMLInputElement ? input : null;
  }

  /** @returns {HTMLDialogElement | null} */
  get dialog() {
    const dialog = this.querySelector('dialog[data-lobos-terms-dialog]');
    return dialog instanceof HTMLDialogElement ? dialog : null;
  }

  /** @returns {Element | null} */
  get gate() {
    const parent = this.parentElement;
    if (!parent) return null;
    for (const element of parent.children) {
      if (element.hasAttribute('data-lobos-terms-gate')) return element;
    }
    return null;
  }

  /**
   * Ustawia pole i przyciski: niezapisany jeszcze wybór klienta albo stan z serwera (atrybut `checked` z Liquid).
   * Stan przywrócony przez przeglądarkę (autouzupełnianie formularzy) nie liczy się.
   */
  sync() {
    const { input } = this;
    if (!input) return;
    const want = desired !== null ? desired : input.defaultChecked;
    if (input.checked !== want) input.checked = want;
    const gate = this.gate;
    if (gate) setLocked(gate, !input.checked);
    if (input.checked) this.hideHint();
  }

  /**
   * Serwer potwierdził stan akceptacji: zapamiętujemy go jako stan domyślny pola.
   * @param {boolean} accepted
   */
  commit(accepted) {
    const { input } = this;
    if (!input) return;
    input.defaultChecked = accepted;
    this.sync();
  }

  /** @param {boolean} [focus] */
  showHint(focus = true) {
    const hint = this.querySelector('.lobos-terms__hint');
    if (hint instanceof HTMLElement) {
      const text = hint.textContent || '';
      hint.textContent = '';
      hint.hidden = false;
      hint.textContent = text;
    }
    if (focus) this.input?.focus({ preventScroll: false });
  }

  hideHint() {
    const hint = this.querySelector('.lobos-terms__hint');
    if (hint instanceof HTMLElement) hint.hidden = true;
  }

  #onChange = (/** @type {Event} */ event) => {
    const { input } = this;
    if (!input || event.target !== input) return;
    desired = input.checked;
    syncAll();
    save(desired, this.dataset.key || KEY, this.dataset.version || '');
  };

  /** Link do regulaminu otwiera okno zamiast nowej strony; nie zmienia stanu pola. */
  #onClick = (/** @type {MouseEvent} */ event) => {
    const target = event.target instanceof Element ? event.target : null;
    const link = target?.closest('a[data-lobos-terms-open]');
    if (!(link instanceof HTMLAnchorElement) || !this.contains(link)) return;
    if (event.defaultPrevented || event.button !== 0) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const { dialog } = this;
    if (!dialog || typeof dialog.showModal !== 'function') return;
    event.preventDefault();
    if (!openTerms(dialog, link)) window.location.assign(link.href);
  };
}

if (!customElements.get('lobos-terms')) {
  customElements.define('lobos-terms', LobosTerms);
}

install();
